import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError, Observable, throwError, switchMap, delay } from 'rxjs';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UsersService } from './services/users.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private userService: UsersService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const currentUrl = req.url;
    const currentPath = window.location.pathname;
    
    // Determine request type: admin, support, or regular user
    const isAdminRequest = currentUrl.includes('/admin') || 
                          currentUrl.includes('admin') || 
                          currentPath.includes('/admin');
    
    const isSupportRequest = currentUrl.includes('/support') || 
                            currentUrl.includes('support') || 
                            currentPath.includes('/support');
    
    // Use different token keys based on request type
    let tokenKey: string;
    let refreshTokenKey: string;
    
    if (isAdminRequest || isSupportRequest) {
      // Both admin and support use the same token keys
      tokenKey = 'authToken';
      refreshTokenKey = 'refreshToken';
    } else {
      tokenKey = 'userToken';
      refreshTokenKey = 'userRefreshToken';
    }
    
    const token = localStorage.getItem(tokenKey);
    const authReq = token
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req.clone();

   // return next.handle(authReq);

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // Check if this is a refresh token request - if so, don't retry to avoid infinite loop
          const isRefreshRequest = currentUrl.includes('/refresh');
          if (isRefreshRequest) {
            this.redirectToAuthorize(isAdminRequest, isSupportRequest);
            return throwError(() => error);
          }
          
          // Check if we're on /karta page and have userCard data
          const isKartaPage = currentPath.includes('/karta');
          const hasUserCard = localStorage.getItem('userCard');
          
          // Skip redirect logic for /karta page when userCard exists
          if (isKartaPage && hasUserCard) {
            return throwError(() => error);
          }
          
          const refreshToken = localStorage.getItem(refreshTokenKey);
          if (refreshToken?.length > 10) {
            // Try to refresh the token and retry the original request
            this.snackBar.open('Sesja wygasła. Odświeżanie tokenu...', 'Zamknij', {
              duration: 5000,
              panelClass: ['success-snackbar'],
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
            }); 

            return this.userService.refreshAccessToken(refreshToken).pipe(
              delay(3000),
              switchMap(tokens => {
                // Save tokens with appropriate keys
                if (isAdminRequest || isSupportRequest) {
                  // Both admin and support use the same token storage methods
                  this.userService.saveAdminToken(tokens.access_token);
                  this.userService.saveAdminRefreshToken(tokens.refresh_token);
                } else {
                  this.userService.saveUserToken(tokens.access_token);
                  this.userService.saveUserRefreshToken(tokens.refresh_token);
                }
                
                // Retry the original request with the new token
                const newAuthReq = req.clone({
                  setHeaders: { Authorization: `Bearer ${tokens.access_token}` }
                });
                return next.handle(newAuthReq);
              }),
              catchError(() => {
                this.redirectToAuthorize(isAdminRequest, isSupportRequest);
                return throwError(() => error);
              })
            );
          } else {
            this.redirectToAuthorize(isAdminRequest, isSupportRequest);
          }
        }
        return throwError(() => error);
      })
    );
  }

  private redirectToAuthorize(isAdminRequest: boolean = false, isSupportRequest: boolean = false) {
    if (isAdminRequest || isSupportRequest) {
      // Both admin and support use the same logout method
      this.userService.logoutAdmin();
    } else {
      this.userService.logoutUser();
    }
    
    this.snackBar.open('Wymagane zalogowanie użytkownika.', 'Zamknij', {
      duration: 8000,
      panelClass: ['error-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });

    const currentUrl = window.location.pathname;
    if (isAdminRequest || currentUrl.includes('admin')) {
      this.router.navigate(['/admin/login']);
    } else if (isSupportRequest || currentUrl.includes('support')) {
      this.router.navigate(['/support/login']);
    } else {
      this.router.navigate(['/logowanie']);
    }
  }
}